import * as React from 'react';
import './tabs.scss';

type TabConfig = {
  /** What the user sees on the tab */
  label: React.ReactNode;
  /** What renders when the tab is selected */
  content: React.ReactNode;

  /** Optional: stable id for keys & a11y (recommended) */
  id?: string;

  /** Optional: disable a tab */
  disabled?: boolean;
};

function a11yProps(index: number, idPrefix = 'basic-tabs') {
  return {
    id: `${idPrefix}-tab-${index}`,
    'aria-controls': `${idPrefix}-tabpanel-${index}`,
  };
}

type CustomTabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
  idPrefix?: string;
};

type BasicTabsProps = {
  tabs: TabConfig[];
  /** Optional: starting tab */
  defaultValue?: number;
  /** Optional: if parent wants to react to tab changes */
  onChangeIndex?: (index: number) => void;

  /** Optional: pass through sx to the root box */
};

function CustomTabPanel(props: CustomTabPanelProps) {
  const { children, value, index, idPrefix = 'basic-tabs', ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${idPrefix}-tabPanel-${index}`}
      aria-labelledby={`${idPrefix}-tab-${index}`}
      {...other}
    >
      {value === index ? <div>{children}</div> : null}
    </div>
  );
}

export default function BasicTabs({
  tabs,
  defaultValue = 0,
  onChangeIndex,
}: BasicTabsProps) {
  // clamp defaultValue to valid range
  const safeDefault = React.useMemo(() => {
    if (!tabs.length) return 0;
    return Math.min(Math.max(defaultValue, 0), tabs.length - 1);
  }, [defaultValue, tabs.length]);

  const [value, setValue] = React.useState(safeDefault);

  // If tabs length changes and current index becomes invalid, fix it.
  React.useEffect(() => {
    if (!tabs.length) return;
    if (value > tabs.length - 1) setValue(tabs.length - 1);
  }, [tabs.length, value]);

  const handleChange = (newValue: number) => {
    setValue(newValue);
    onChangeIndex?.(newValue);
  };

  const idPrefix = 'basic-tabs';

  return (
    <div>
      <div role="tablist" aria-label="config tabs">
        {tabs.map((t, i) => (
          <button
            className={`TAB__BUTTON`}
            key={t.id ?? i}
            role="tab"
            type="button"
            aria-selected={value === i}
            aria-controls={`${idPrefix}-tabPanel-${i}`}
            id={`${idPrefix}-tab-${i}`}
            disabled={t.disabled}
            onClick={() => handleChange(i)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tabs.map((t, i) => (
        <CustomTabPanel
          key={t.id ?? i}
          value={value}
          index={i}
          idPrefix={idPrefix}
        >
          {t.content}
        </CustomTabPanel>
      ))}
    </div>
  );
}
